import { useTranslation } from 'react-i18next';
import { ArrowRight, PartyPopper } from 'lucide-react';
import { Badge, Button } from '../ui';
import { useStepNavigation } from './navigation';

/**
 * The always-visible next step (spec §9.4 "sichtbarer nächster Schritt").
 *
 * It stays on screen in both the overview and inside a path, so the answer to
 * "where was I?" is never more than a glance away and an interrupted session
 * costs nothing to resume (§9.4, §11.1). Required work is picked before
 * voluntary depth — see `findNextStep`.
 *
 * The action either jumps into the step's destination (a mission run, a file
 * in Wissen, a question in Chat) or, for a step only the user can judge,
 * opens the chapter it lives in.
 */
export function NextStep({ next, onOpenPath }) {
  const { t } = useTranslation();
  const openStep = useStepNavigation();

  if (!next) {
    return (
      <aside className="etch-next-step" data-state="done" aria-label={t('onboarding.nextStep')}>
        <span className="etch-next-step__icon" aria-hidden="true">
          <PartyPopper size={18} />
        </span>
        <div className="etch-next-step__body">
          <span className="etch-eyebrow">{t('onboarding.nextStep')}</span>
          <p className="etch-next-step__title">{t('onboarding.nextStepNone')}</p>
        </div>
      </aside>
    );
  }

  const { path, chapter, step } = next;
  const pathTitle = path.titleKey ? t(path.titleKey) : path.title;
  const chapterTitle = chapter.titleKey ? t(chapter.titleKey) : chapter.title;

  return (
    <aside className="etch-next-step" data-state="open" aria-label={t('onboarding.nextStep')}>
      <span className="etch-next-step__icon" aria-hidden="true">
        <ArrowRight size={18} />
      </span>
      <div className="etch-next-step__body">
        <span className="etch-eyebrow">{t('onboarding.nextStep')}</span>
        <p className="etch-next-step__title">{step.title}</p>
        <p className="etch-next-step__meta">
          {t('onboarding.nextStepIn', { path: pathTitle, chapter: chapterTitle })}
          <Badge tone={step.required ? 'brand' : undefined}>
            {step.required ? t('onboarding.required') : t('onboarding.optional')}
          </Badge>
          <Badge>{t(`onboarding.kind.${step.kind}`)}</Badge>
        </p>
      </div>
      <div className="etch-next-step__action">
        {step.to && step.actionKey ? (
          <Button variant="primary" size="compact" onClick={() => openStep(step)}>
            {t(step.actionKey)}
          </Button>
        ) : (
          <Button variant="secondary" size="compact" onClick={() => onOpenPath(path)}>
            {t('onboarding.resume')}
            <span className="etch-visually-hidden"> — {step.title}</span>
          </Button>
        )}
      </div>
    </aside>
  );
}

export default NextStep;
